import {
  useCallback, useMemo,
} from 'react';
import { convertPolygonToGeometry } from '~/_shared/types/polygon/polygon.utils';
import { useLatest } from '~/_shared/utils/hooks/useLatest';
import { useLatLngSpreadsheetData } from '../../../map/map/useSpreadsheetData.hook';
import { createProximityFilterArgumentsFromProximity } from '../../../proximity/proximity.helpers';
import { useProximities } from '../../../proximity/useProximities';
import { type ActiveProximity } from '../../../sidebar/sidebarApps/rightSidebar/rightSidebarConfiguration';
import { type PolygonFilterRequest } from '../../../spreadsheet/filter/polygon/spreadsheetPolygonFilter.types';
import { createRadiusFilterItemRequest } from '../../../spreadsheet/filter/radius/spreadsheetFilterRadius.factory';
import { type RadiusFilterRequest } from '../../../spreadsheet/filter/radius/spreadsheetFilterRadius.types';
import { useProximityMetricsSelector } from '../../../store/mapSettings/proximity/mapSettingsProximity.selectors';
import {
  isDriveTimePolygon, isIndividualRadius,
} from '../../types/proximity/proximity.types';
import { useDataLayersMetrics } from './dataLayer/useDataLayersMetrics';
import { validateDataLayerProximityArea } from './dataLayer/validateDataLayerArea';
import { type MetricModel } from './metrics.factory';
import { useMetricsData } from './useMetricsData';

export const useProximityMetricsData = (activeMetric: ActiveProximity) => {
  const proximityMetrics: MetricModel[] = useProximityMetricsSelector();
  const dataLayerMetrics = useDataLayersMetrics();
  const proximitiesData = useProximities();
  const latLngLookup = useLatLngSpreadsheetData();

  const allMetrics = useMemo(() => [...proximityMetrics, ...dataLayerMetrics], [proximityMetrics, dataLayerMetrics]);

  const activeProximity = proximitiesData.find(proximity => proximity.id === activeMetric.proximityId);

  const radiusFilterRequest: RadiusFilterRequest | null = useMemo(() => {
    if (!activeProximity) {
      return null;
    }

    const { circles } = createProximityFilterArgumentsFromProximity(
      activeProximity,
      latLngLookup,
      activeMetric.spreadsheetRowId ?? null,
    );

    if (circles.length === 0) {
      return null;
    }

    return {
      type: 'or',
      radiuses: circles.map(request => createRadiusFilterItemRequest(request)),
    };
  }, [activeProximity, activeMetric.spreadsheetRowId, latLngLookup]);

  const polygonFilterRequest: PolygonFilterRequest | null = useMemo(() => {
    if (!activeProximity) {
      return null;
    }

    if (isDriveTimePolygon(activeProximity) && activeProximity.data.paths) {
      return {
        type: 'or',
        polygons: activeProximity.data.paths.map(polygon => ({ path: convertPolygonToGeometry(polygon) })),
      };
    }

    return null;
  }, [activeProximity]);

  // Inputs are read lazily so the callback stays stable.
  // Refetch on proximity change is already triggered by the filter requests (same inputs).
  const validationInputsRef = useLatest({ activeProximity, spreadsheetRowId: activeMetric.spreadsheetRowId, latLngLookup });
  const validateDataLayerArea = useCallback(async () => {
    const { activeProximity, spreadsheetRowId, latLngLookup } = validationInputsRef.current;

    if (!activeProximity) {
      return { isValid: true, supportedRegions: [] } as const;
    }

    // a spreadsheet row's location, when available, takes priority over the proximity's own center
    const spreadsheetCenter = spreadsheetRowId ? latLngLookup.getRow(spreadsheetRowId) : undefined;
    const proximityCenter = isIndividualRadius(activeProximity) ? activeProximity.data.data : undefined;

    return validateDataLayerProximityArea(activeProximity, spreadsheetCenter ?? proximityCenter, spreadsheetRowId);
  }, [validationInputsRef]);

  return useMetricsData({
    selectedMetrics: allMetrics,
    radiusFilterRequest: radiusFilterRequest ?? undefined,
    polygonFilterRequest: polygonFilterRequest ?? undefined,
    validateDataLayerArea,
  });
};
